import { AlertTriangleIcon, InfoIcon } from 'assets/icons';
import globalStyles from 'assets/stylesheets/global-styles.module.scss';
import cardStyles from 'common/settings-layout/settings-card.module.scss';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { HelperText } from 'common/helper-text';
import { LabeledText } from 'common/labeled-text';
import { LinkButton } from 'common/link-button';
import { ProgressBar } from 'common/progress-bar';
import { contactLink } from 'config/constants';
import dayjs from 'dayjs';
import { planName } from 'helpers/plan-name';
import { formatBytes, hasStoragePlan } from 'helpers/storage';
import { classnames } from 'helpers/utils';
import { useMemo } from 'react';
import { goToPage, RouteName } from 'routes';
import aboutPageStyles from '../../about-vault.module.scss';
import styles from './subscription-section.module.scss';

type SubscriptionSectionProps = {
  vault: VaultType;
  isOwner: boolean;
};

// Storage usage above this share of the plan is highlighted as running out.
const STORAGE_ALERT_PERCENT = 90;

// The vault's plan and what it's using. Plans are storage-based (sold through
// Stripe): a size, paid once or yearly, with AI credits attached. The old
// tier machinery (video-call time, clips, modify/cancel flows) is gone.
const SubscriptionSection = ({ vault, isOwner }: SubscriptionSectionProps) => {
  const onStoragePlan = hasStoragePlan(vault);
  // Only Annual plans renew; One-time and legacy vaults have nothing to bill.
  const renews = onStoragePlan && vault.storageKind === 'temporary';

  const infoWarningMessage = useMemo(() => {
    if (vault.subscriptionStatus === 'subscription_canceled') {
      return (
        <div
          className={classnames(
            'text__body__regular__small__gray800',
            styles.subscriptionWarning,
          )}
        >
          <AlertTriangleIcon className={styles.alertIcon} />
          <span>
            The subscription has been cancelled and will run out on{' '}
            {dayjs(vault.nextPaymentDate).format('MM/DD/YYYY')}. If you want to
            be reinstated for payment please&nbsp;
            <a href={contactLink} target="_blank" rel="noreferrer">
              contact support
            </a>
            .
          </span>
        </div>
      );
    }

    if (vault.subscriptionStatus === 'subscription_overdue') {
      return (
        <div
          className={classnames(
            'text__body__regular__small__gray800',
            styles.subscriptionWarning,
          )}
        >
          <AlertTriangleIcon className={styles.alertIcon} />
          <span>
            Your card couldn’t be charged for your last bill. Please{' '}
            <Button
              buttonSize={ButtonSize.Small}
              buttonStyle={ButtonStyle.SecondaryGhostLink}
              onClick={() => {
                goToPage(RouteName.Account);
              }}
            >
              review your payment method
            </Button>
            .
          </span>
        </div>
      );
    }

    return null;
  }, [vault.subscriptionStatus, vault.nextPaymentDate]);

  const renewalMessage = () => {
    if (vault.subscriptionStatus === 'subscription_overdue') {
      return 'Expired on';
    }
    if (vault.subscriptionStatus === 'subscription_canceled') {
      return 'Expiration due';
    }
    return 'Renewal due';
  };

  const usedBytes = vault.totalStorage;
  const percentUsed = onStoragePlan
    ? Math.min(100, Math.round((100 * usedBytes) / vault.storageLimit))
    : 0;
  const reachingStorageLimit = percentUsed >= STORAGE_ALERT_PERCENT;

  const showStorageUsage = () => (
    <div className={styles.progressBar}>
      <div
        className={classnames(
          styles.progressTitle,
          'text__body__regular__overline__textNeutral60',
        )}
      >
        Storage
      </div>
      <ProgressBar
        percentageUsed={percentUsed}
        reachingLimit={reachingStorageLimit}
      />
      <div className={styles.progressTimes}>
        <span className={styles.usedTime}>
          <span className="text__body__regular__small__textNeutral60">
            Used:&nbsp;
          </span>
          <span className="text__body__regular__small__textNeutral70">
            {formatBytes(usedBytes)}
          </span>
        </span>
        <span className={styles.maxTime}>
          <span className="text__body__regular__small__textNeutral60">
            Remaining:&nbsp;
          </span>
          <span className="text__body__regular__small__textNeutral70">
            {formatBytes(Math.max(vault.storageLimit - usedBytes, 0))}
          </span>
        </span>
      </div>
    </div>
  );

  const helperTextContent = () => {
    if (!onStoragePlan) {
      return (
        <span className="text__body__regular__small__textNeutral60">
          This vault is on a legacy plan with no storage limit.
        </span>
      );
    }
    if (reachingStorageLimit) {
      return (
        <span className="text__body__regular__small__gray700">
          This vault is almost full. Upgrade to a bigger plan to keep adding
          memories.
        </span>
      );
    }
    return (
      <span className="text__body__regular__small__textNeutral60">
        Every vault includes unlimited collaborators and all recording tools.
        Upgrade any time for more storage.
      </span>
    );
  };

  // TODO: point this at the storage-plan upgrade page once it exists. The old
  // tier-based "modify subscription" flow doesn't apply to storage plans, so
  // until then the button is disabled rather than starting the wrong flow.
  const canUpgrade =
    isOwner &&
    onStoragePlan &&
    vault.subscriptionStatus === 'subscription_active';

  return (
    <div className={classnames(cardStyles.card, styles.subscriptionSection)}>
      <p className="text__title2__textNeutral70">Subscription</p>
      <div className={styles.infoBar}>
        <div className={styles.vaultDateAndRenewalColumn}>
          <LabeledText
            label="Vault created"
            text={dayjs(vault.createdAt).format('MM/DD/YYYY')}
            vertical
            labelClassName={styles.customLabelColor}
            textClassName={styles.customTextColor}
          />

          {isOwner && renews && vault.nextPaymentDate && (
            <div className={styles.labeledText}>
              <span className="text__body__semi__bold__medium__gray800">
                {renewalMessage()}:
              </span>
              <span
                className={
                  vault.subscriptionStatus === 'subscription_overdue'
                    ? classnames(
                        'text__body__semi__bold__medium__gray800',
                        styles.orangeText,
                      )
                    : 'text__body__regular__small__gray700'
                }
              >
                {dayjs(vault.nextPaymentDate).format('MM/DD/YYYY')}
              </span>
            </div>
          )}
        </div>

        <div className={styles.subscriptionAndBillingColumn}>
          <LabeledText
            label="Plan"
            text={planName(vault)}
            vertical
            labelClassName={styles.customLabelColor}
            textClassName={styles.customTextColor}
          />

          {isOwner &&
            renews &&
            vault.subscriptionStatus !== 'subscription_canceled' &&
            !!vault.nextPaymentAmount && (
              <LabeledText
                label="Next billing"
                text={`$${vault.nextPaymentAmount.toFixed(2)}`}
                vertical
                labelClassName={styles.customLabelColor}
                textClassName={styles.customTextColor}
              />
            )}
        </div>
      </div>

      {infoWarningMessage}

      <div className={globalStyles.divider} />

      <p className="text__title2__textNeutral70">Usage</p>
      {onStoragePlan && (
        <div className={styles.progressSection}>{showStorageUsage()}</div>
      )}
      <HelperText
        content={helperTextContent()}
        icon={<InfoIcon className={styles.infoIcon} />}
      />
      {canUpgrade && (
        <div className={styles.buttonColumn}>
          <Button
            buttonStyle={ButtonStyle.PrimaryStroke}
            disabled
            title="Plan upgrades are coming soon"
            className={classnames({
              [styles.orangeLink]: reachingStorageLimit,
            })}
          >
            Upgrade subscription
          </Button>
        </div>
      )}

      {/*
        Active discount — hidden while no discount program is running.
        Restore by uncommenting this block and the related imports
        (DiscountCodeItem, DiscountCodeDetailsModal, isExpiredDate,
        subscriptionDeals from AppContext).

        <div className={globalStyles.divider} />
        <p className="text__title2__textNeutral70">Active discount</p>
        <HelperText
          content={
            <span className="text__body__regular__small__textNeutral60">
              Only one discount code may be applied to an individual vault.
              We apply the best deal based on the codes you supply.
            </span>
          }
          icon={<InfoIcon className={styles.infoIcon} />}
        />
        {showDiscountCodesList(subscriptionDeals)}
        <div className={aboutPageStyles.buttonSection}>
          <LinkButton onClick={() => goToPage(RouteName.Account)}>
            Modify in account settings
          </LinkButton>
        </div>
      */}

      {isOwner && (
        <>
          <div className={globalStyles.divider} />

          <div className={aboutPageStyles.buttonSection}>
            <LinkButton
              buttonStyle={ButtonStyle.LinkColor}
              onClick={() => goToPage(RouteName.Account)}
            >
              Payment history
            </LinkButton>
            <LinkButton
              buttonStyle={ButtonStyle.LinkColor}
              onClick={() => goToPage(RouteName.Account)}
            >
              Review payment method in account settings
            </LinkButton>
            <LinkButton
              buttonStyle={ButtonStyle.LinkColor}
              onClick={() => window.open(contactLink, '_blank')}
            >
              Contact support for account transferring
            </LinkButton>
          </div>
        </>
      )}
    </div>
  );
};

export { SubscriptionSection };
